'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  initMetaPixel,
  META_CONSENT_GRANTED_EVENT,
  META_CONSENT_REVOKED_EVENT,
  revokeMetaConsent,
  trackMetaPageView,
  trackMetaViewContent,
} from '@/src/lib/meta/meta-pixel';

type Props = {
  pixelId: string;
  initialConsentGranted: boolean;
};

// Activity detail surfaces: booking tunnel entry per activity type, and
// product detail pages. Route-derived names only — no personal data.
function viewContentNameForPath(pathname: string): string | null {
  const bookingMatch = pathname.match(
    /^\/[a-z]{2}\/booking\/(atelier|rdv|anniversaire|entreprise)(?:\/|$)/
  );
  if (bookingMatch) return `booking_${bookingMatch[1]}`;

  const productMatch = pathname.match(/^\/[a-z]{2}\/products\/([^/]+)\/?$/);
  if (productMatch) return `product_${decodeURIComponent(productMatch[1])}`;

  return null;
}

export function MetaPixel({ pixelId, initialConsentGranted }: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const startedRef = useRef(false);
  const lastTrackedKeyRef = useRef<string | null>(null);

  // Consent lifecycle: load the pixel only once consent exists (either known
  // server-side at mount, or given later via the cookie banner).
  useEffect(() => {
    const handleGranted = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      initMetaPixel(pixelId);
      // initMetaPixel already fired the PageView for this page; register the
      // route and send ViewContent if this is an activity detail page.
      lastTrackedKeyRef.current = `${window.location.pathname}${window.location.search}`;
      const contentName = viewContentNameForPath(window.location.pathname);
      if (contentName) trackMetaViewContent({ contentName });
    };
    const handleRevoked = () => revokeMetaConsent();

    if (initialConsentGranted) handleGranted();

    window.addEventListener(META_CONSENT_GRANTED_EVENT, handleGranted);
    window.addEventListener(META_CONSENT_REVOKED_EVENT, handleRevoked);

    return () => {
      window.removeEventListener(META_CONSENT_GRANTED_EVENT, handleGranted);
      window.removeEventListener(META_CONSENT_REVOKED_EVENT, handleRevoked);
    };
  }, [pixelId, initialConsentGranted]);

  // SPA navigation: fire a PageView on every client-side route change, plus
  // ViewContent when landing on an activity detail page.
  const queryString = searchParams?.toString() ?? '';
  useEffect(() => {
    if (!startedRef.current) return;

    const key = `${pathname}${queryString ? `?${queryString}` : ''}`;
    if (lastTrackedKeyRef.current === key) return;

    const isInitialRoute = lastTrackedKeyRef.current === null;
    lastTrackedKeyRef.current = key;

    if (!isInitialRoute) {
      trackMetaPageView();
    }

    const contentName = viewContentNameForPath(pathname);
    if (contentName) trackMetaViewContent({ contentName });
  }, [pathname, queryString]);

  return null;
}
